import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { EVENT_ICON_TYPES } from "@/lib/event-icons";
import {
  MagnifyingGlass,
  Funnel,
  FireSimple,
  TreeStructure,
  Columns,
  FileArrowUp,
  SidebarSimple,
  DownloadSimple,
  UsersThree,
  ClockCounterClockwise,
  Plus,
  CheckSquare,
  Lifebuoy,
} from "@phosphor-icons/react";
import { SupportTicketDialog } from "@/components/HaveAQuestionBadge";

const SECTIONS = [
  {
    title: "Navigation",
    tips: [
      {
        icon: SidebarSimple,
        label: "Toggle task table",
        body: "Collapse the left pane to see more of the timeline, or drag the vertical divider to resize it.",
      },
      {
        icon: MagnifyingGlass,
        label: "Search",
        body: "Filter tasks and subtasks by typing in the search bar. Parent tasks stay in context for matching subtasks.",
      },
      {
        icon: Funnel,
        label: "Workstream & Status filters",
        body: "Use the dropdowns to narrow to a single team or status. Click “Clear Filters” to reset.",
      },
    ],
  },
  {
    title: "Tasks",
    tips: [
      {
        icon: Plus,
        label: "Add tasks",
        body: "Use the + button in the toolbar or the dashed “Add Task” row at the bottom of the task list.",
      },
      {
        icon: TreeStructure,
        label: "Add subtasks in bulk",
        body: "Hover the Subtask cell and click +. Paste a list (one per line) and press ⌘/Ctrl+Enter to add them all.",
      },
      {
        icon: CheckSquare,
        label: "Bulk select & delete",
        body: "Click “Select” in the toolbar to show checkboxes. Tick rows, then press “Delete N” to remove them in one go.",
      },
    ],
  },
  {
    title: "Timeline",
    tips: [
      {
        icon: FireSimple,
        label: "Risk Heatmap",
        body: "Rolls up risk + issue events by week. Click a cell to jump to the relevant task.",
      },
      {
        icon: FireSimple,
        label: "Health Tracker (stoplight)",
        body: "Health is computed across every open task (status ≠ Done). A task whose due date has passed by 20+ days lights up RED (off track); 10–19 days = YELLOW (at risk); otherwise GREEN (healthy). The project header badge always shows the worst tier — fix the offender(s) and the badge improves. Toggle the feature on/off by clicking the badge in the project header.",
      },
      {
        icon: TreeStructure,
        label: "Critical Path",
        body: "Highlights the longest chain of dependent tasks in red so you know where slippage hurts most.",
      },
      {
        icon: Columns,
        label: "Custom columns & dates",
        body: "Add as many date columns as you need. Pair two (like Start & Due) and they render as a single bar.",
      },
    ],
  },
  {
    title: "Collaboration",
    tips: [
      {
        icon: UsersThree,
        label: "Share & roles",
        body: "Invite people by email with Admin / Edit / View access. Use the Share button in the header.",
      },
      {
        icon: ClockCounterClockwise,
        label: "Versions",
        body: "Save named snapshots and restore any prior state. Great before big replans.",
      },
      {
        icon: FileArrowUp,
        label: "Import & export",
        body: "Import .csv / .xlsx and export the chart to PDF or PNG from the header menu.",
      },
    ],
  },
];

export default function HelpDrawer({ open, onOpenChange, projectId }) {
  const [ticketOpen, setTicketOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[440px] p-0 bg-white border-l border-slate-200 overflow-y-auto"
        data-testid="help-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 text-left">
          <SheetTitle className="font-display text-2xl font-black tracking-tight text-slate-950">
            How to use this Gantt
          </SheetTitle>
          <SheetDescription className="text-sm text-slate-600 leading-relaxed">
            Drop icons on any date to flag risks, milestones, decisions and more. Hover
            an icon on the chart to read its comment. Here's the full legend and a few
            tips for the rest of the app.
          </SheetDescription>
          <Button
            onClick={() => setTicketOpen(true)}
            className="mt-4 h-11 rounded-md text-white font-medium w-full"
            style={{ background: "#4f56ff" }}
            data-testid="help-drawer-open-ticket"
          >
            <Lifebuoy size={16} weight="bold" />
            <span className="ml-2">Create a support ticket</span>
          </Button>
          <p className="text-[11px] text-slate-500 mt-2">
            Stuck on something specific? Open a ticket and we'll get back to you.
          </p>
        </SheetHeader>

        <section className="px-6 py-5 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mb-3">
            Icon Legend
          </div>
          <ul className="space-y-2.5" data-testid="help-icon-legend">
            {EVENT_ICON_TYPES.map((o) => {
              const Icon = o.icon;
              return (
                <li
                  key={o.type}
                  className="flex items-start gap-3"
                  data-testid={`legend-${o.type}`}
                >
                  <div
                    className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center border-2 border-white shadow-sm"
                    style={{ background: o.color }}
                  >
                    <Icon size={15} weight="fill" className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-950 leading-tight">
                      {o.label}
                    </div>
                    <div className="text-xs text-slate-600 leading-snug mt-0.5">
                      {o.description}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-900">How to add an icon:</span>{" "}
            click any day cell on the timeline (or the hover “+” at the right of a row),
            pick a type, and optionally attach a comment. Hover the icon later to read
            the comment back.
          </div>
        </section>

        {SECTIONS.map((sec) => (
          <section key={sec.title} className="px-6 py-5 border-b border-slate-100 last:border-b-0">
            <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mb-3">
              {sec.title}
            </div>
            <ul className="space-y-3">
              {sec.tips.map((t) => {
                const Icon = t.icon;
                return (
                  <li key={t.label} className="flex items-start gap-3">
                    <div className="shrink-0 w-7 h-7 rounded-md bg-slate-950 text-white flex items-center justify-center">
                      <Icon size={13} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-950 leading-tight">
                        {t.label}
                      </div>
                      <div className="text-xs text-slate-600 leading-snug mt-0.5">
                        {t.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <section className="px-6 py-5 text-[11px] text-slate-500 font-mono-ibm uppercase tracking-widest flex items-center gap-2">
          <DownloadSimple size={12} weight="bold" />
          Export · PDF / PNG from the header menu
        </section>
      </SheetContent>
      <SupportTicketDialog
        open={ticketOpen}
        onOpenChange={setTicketOpen}
        projectId={projectId}
      />
    </Sheet>
  );
}
